import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import PlaygroundTabs from './components/PlaygroundTabs';
import ReactPlayground from './components/ReactPlayground';
import APITester from './components/APITester';
import AlgorithmVisualizer from './components/AlgorithmVisualizer';
import CSSPlayground from './components/CSSPlayground';
import OpenSourceSection from './components/OpenSourceSection';
import TutorialSection from './components/TutorialSection';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const CodeLaboratoryInteractivePlayground = () => {
  const [activeTab, setActiveTab] = useState('react-playground');
  const [theme, setTheme] = useState('dark');

  const tabs = [
    {
      id: 'react-playground',
      label: 'React Playground',
      icon: 'Code2',
      badge: 'Interactive'
    },
    {
      id: 'api-tester',
      label: 'API Tester',
      icon: 'Zap'
    },
    {
      id: 'algorithm-visualizer',
      label: 'Algorithm Visualizer',
      icon: 'BarChart3',
      badge: 'Visual'
    },
    {
      id: 'css-playground',
      label: 'CSS Playground',
      icon: 'Palette'
    },
    {
      id: 'open-source',
      label: 'Open Source',
      icon: 'GitBranch'
    },
    {
      id: 'tutorials',
      label: 'Tutorials',
      icon: 'BookOpen',
      badge: 'New'
    }
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'react-playground':
        return <ReactPlayground />;
      case 'api-tester':
        return <APITester />;
      case 'algorithm-visualizer':
        return <AlgorithmVisualizer />;
      case 'css-playground':
        return <CSSPlayground />;
      case 'open-source':
        return <OpenSourceSection />;
      case 'tutorials':
        return <TutorialSection />;
      default:
        return <ReactPlayground />;
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className={`min-h-screen bg-background ${theme === 'dark' ? 'dark' : ''}`}>
      <Header />
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center shadow-large">
                  <Icon name="Terminal" size={32} color="white" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-success rounded-full flex items-center justify-center">
                  <Icon name="Zap" size={12} color="white" />
                </div>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6">
              Code Laboratory
              <span className="block text-accent mt-2">Interactive Playground</span>
            </h1>
            
            <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8 leading-relaxed">
              Explore technical capabilities hands-on through interactive coding environments. 
              Experiment with React components, test APIs, visualize algorithms, and discover 
              the creative possibilities of modern web development.
            </p>
            
            <div className="flex items-center justify-center space-x-4">
              <Button
                variant="default"
                size="lg"
                iconName="Play"
                iconPosition="left"
                onClick={() => setActiveTab('react-playground')}
                className="bg-accent hover:bg-hover text-accent-foreground shadow-large"
              >
                Start Coding
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="GitBranch"
                iconPosition="left"
                onClick={() => setActiveTab('open-source')}
              >
                View Projects
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-accent mb-2">50+</div>
              <div className="text-sm text-text-secondary">Code Examples</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-success mb-2">15+</div>
              <div className="text-sm text-text-secondary">Algorithms</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-warning mb-2">25+</div>
              <div className="text-sm text-text-secondary">Tutorials</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">100%</div>
              <div className="text-sm text-text-secondary">Interactive</div>
            </div>
          </div>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-primary">
                Interactive Development Environment
              </h2>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName={theme === 'dark' ? 'Sun' : 'Moon'}
                  onClick={toggleTheme}
                  className="text-text-secondary hover:text-primary"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Settings"
                  className="text-text-secondary hover:text-primary"
                />
              </div>
            </div>
            
            <PlaygroundTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          {/* Tab Content */}
          <div className="bg-background rounded-lg border border-border shadow-soft overflow-hidden">
            <div className="p-6 lg:p-8">
              {renderActiveTab()}
            </div>
          </div>
        </div>
      </section>
      {/* Features Overview */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary mb-4">
              Why Use the Code Laboratory?
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Experience the power of interactive learning and hands-on experimentation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-background rounded-lg border border-border p-6 text-center hover:shadow-medium transition-all duration-300">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name="Zap" size={24} className="text-accent" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Real-time Execution</h3>
              <p className="text-text-secondary text-sm">
                See your code changes instantly with live preview and immediate feedback
              </p>
            </div>

            <div className="bg-background rounded-lg border border-border p-6 text-center hover:shadow-medium transition-all duration-300">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name="Eye" size={24} className="text-success" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Visual Learning</h3>
              <p className="text-text-secondary text-sm">
                Understand complex algorithms through interactive visualizations
              </p>
            </div>

            <div className="bg-background rounded-lg border border-border p-6 text-center hover:shadow-medium transition-all duration-300">
              <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name="BookOpen" size={24} className="text-warning" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Comprehensive Tutorials</h3>
              <p className="text-text-secondary text-sm">
                Learn from detailed tutorials with practical examples and best practices
              </p>
            </div>

            <div className="bg-background rounded-lg border border-border p-6 text-center hover:shadow-medium transition-all duration-300">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name="GitBranch" size={24} className="text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Open Source</h3>
              <p className="text-text-secondary text-sm">
                Explore real-world projects and contribute to the developer community
              </p>
            </div>

            <div className="bg-background rounded-lg border border-border p-6 text-center hover:shadow-medium transition-all duration-300">
              <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name="Palette" size={24} className="text-purple-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Creative Playground</h3>
              <p className="text-text-secondary text-sm">
                Experiment with CSS animations, layouts, and modern web technologies
              </p>
            </div>

            <div className="bg-background rounded-lg border border-border p-6 text-center hover:shadow-medium transition-all duration-300">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name="Code2" size={24} className="text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Multi-Language Support</h3>
              <p className="text-text-secondary text-sm">
                Work with React, TypeScript, CSS, and modern JavaScript features
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-accent/10 to-primary/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-primary mb-4">
            Ready to Start Experimenting?
          </h2>
          <p className="text-lg text-text-secondary mb-8">
            Dive into the interactive playground and discover what you can build
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Button
              variant="default"
              size="lg"
              iconName="Terminal"
              iconPosition="left"
              onClick={() => setActiveTab('react-playground')}
              className="bg-accent hover:bg-hover text-accent-foreground shadow-large"
            >
              Open React Playground
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="MessageCircle"
              iconPosition="left"
              onClick={() => window.location.href = '/contact-opportunities'}
            >
              Discuss a Project
            </Button>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                <Icon name="Code2" size={16} color="white" />
              </div>
              <span className="font-semibold">Arvin Z's Portfolio</span>
            </div>
            <div className="text-sm opacity-80">
              © {new Date()?.getFullYear()} Arvin Z's Portfolio. Built with passion for code.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CodeLaboratoryInteractivePlayground;